"use client";

/**
 * Chat-bar Skills button — opens a browser of skills (Public + My Skills).
 *
 * Picking a skill ARMS the chat bar with that skill (no-blob launch): the
 * parent composer shows a skill chip and the user types their own input. The
 * skill's engineered instruction is NEVER pasted into the chat — it is sent as
 * `skill_id` and injected server-side. See:
 *   - components/multimodal-input.tsx (onPick → setActiveSkill)
 *   - components/chat.tsx (skill_id in experimental_prepareRequestBody)
 *   - backend app/services/agent_service.py (resolves agent_instruction)
 *
 * If no `onPick` is provided (used outside the composer), it falls back to a
 * `/?skill=<id>` deep link which arms the chat bar on the home page.
 *
 * Styling mirrors the Data selector: near-black surface, hairline borders,
 * emerald accent, white text (NO gray), slim rows, sharp corners.
 * (.cursor/rules/xynth-frontend-copy-and-styling.mdc)
 */

import * as React from "react";
import { useRouter } from "next/navigation";
import {
  Sparkles,
  Zap,
  Hammer,
  Search,
  Library,
  HelpCircle,
  FileText,
  CandlestickChart,
  Newspaper,
  TrendingUp,
  Landmark,
  Bell,
  Target,
  Wallet,
  BarChart3,
  type LucideIcon,
} from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "./ui/popover";
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";
import { usePersonalSkills } from "@/contexts/PersonalSkillsContext";
import { apiClient, type PublicSkill } from "@/lib/api-client";
import { useIsMobile } from "@/hooks/use-is-mobile";
import { cn } from "@/lib/utils";

const CREATE_SKILL_ID = "create-a-skill";

type PickedSkill = { id: string; name: string };

type Row = { id: string; name: string };

// No icon field exists on skill docs, so pick a lucide icon from keywords in
// the skill name. First match wins; Sparkles is the fallback.
const ICON_RULES: Array<[RegExp, LucideIcon]> = [
  [/sec|filing|10-k|10-q/i, FileText],
  [/option/i, CandlestickChart],
  [/news|search/i, Newspaper],
  [/trade|enter|swing|momentum/i, TrendingUp],
  [/congress|politician|insider|fund/i, Landmark],
  [/alert|automation|watch|monitor/i, Bell],
  [/earnings|deep dive|analysis|report/i, BarChart3],
  [/portfolio|dividend|cash/i, Wallet],
  [/screen|scanner|radar|find/i, Target],
];

function iconForSkill(name: string): LucideIcon {
  for (const [re, Icon] of ICON_RULES) {
    if (re.test(name)) return Icon;
  }
  return Sparkles;
}

export function SkillsButton({
  compact = false,
  onPick,
}: {
  compact?: boolean;
  /** Arm the chat bar with this skill (no-blob). When omitted, falls back to a
   *  `/?skill=` deep link. */
  onPick?: (skill: PickedSkill) => void;
}) {
  const router = useRouter();
  const { skills } = usePersonalSkills();
  const isMobile = useIsMobile();
  const [open, setOpen] = React.useState(false);
  const [tab, setTab] = React.useState<"public" | "mine">("public");
  const [query, setQuery] = React.useState("");
  const [publicSkills, setPublicSkills] = React.useState<PublicSkill[]>([]);
  const [loadingPublic, setLoadingPublic] = React.useState(false);

  // Lazy-load the public catalog the first time the popover opens.
  React.useEffect(() => {
    if (!open || publicSkills.length > 0 || loadingPublic) return;
    setLoadingPublic(true);
    apiClient
      .listPublicSkills({ limit: 50 })
      .then((res) => {
        setPublicSkills(res.skills || []);
        // eslint-disable-next-line no-console
        console.log("[SKILL-LAUNCH] loaded public skills for browser", {
          count: (res.skills || []).length,
        });
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.log("[SKILL-LAUNCH] failed to load public skills", err);
      })
      .finally(() => setLoadingPublic(false));
  }, [open, publicSkills.length, loadingPublic]);

  const pick = (skill: PickedSkill) => {
    // eslint-disable-next-line no-console
    console.log("[SKILL-LAUNCH] SkillsButton pick", { skill, hasOnPick: Boolean(onPick) });
    setOpen(false);
    if (onPick) {
      onPick(skill);
      return;
    }
    // Fallback: deep link that arms the chat bar on the home page (no blob).
    const params = new URLSearchParams({ skill: skill.id, skillname: skill.name });
    router.push(`/?${params.toString()}`);
  };

  const openDetail = (id: string) => {
    setOpen(false);
    router.push(`/skills/${id}`);
  };

  const publicRows: Row[] = publicSkills.map((s) => ({ id: s.id, name: s.name }));

  const mineRows: Row[] = skills
    .filter((s) => s.id !== CREATE_SKILL_ID)
    .map((s) => ({ id: s.id, name: s.name }));

  const rows = (tab === "public" ? publicRows : mineRows).filter((r) =>
    query.trim() ? r.name.toLowerCase().includes(query.trim().toLowerCase()) : true
  );

  const trigger = compact ? (
    <button
      type="button"
      aria-label="Skills"
      className="flex items-center justify-center p-1.5 rounded-md text-white/80 transition-all duration-150 hover:bg-zinc-800/60 hover:text-white"
    >
      <Sparkles size={14} />
    </button>
  ) : (
    <button
      type="button"
      aria-label="Skills"
      className="flex items-center gap-1.5 rounded-md border border-transparent px-2.5 py-1.5 text-white/80 transition-all duration-150 hover:bg-zinc-800/60 hover:text-white"
    >
      <Sparkles size={14} />
      <span className="text-[11px] font-semibold leading-none tracking-wide whitespace-nowrap">
        Skills
      </span>
    </button>
  );

  const body = (
    <>
        {/* Tabs */}
        <div className="flex items-center gap-1 px-1 pb-1.5 pt-1">
          {(["public", "mine"] as const).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={cn(
                "rounded-md px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide transition-colors",
                tab === t
                  ? "bg-emerald-500/15 text-emerald-300"
                  : "text-white/80 hover:bg-white/[0.05] hover:text-white"
              )}
            >
              {t === "public" ? "Public" : "My skills"}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="mb-1.5 flex items-center gap-1.5 rounded-md border border-white/10 bg-white/[0.03] px-2 py-1.5">
          <Search className="size-3.5 text-white/80" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search skills"
            className="w-full bg-transparent text-[13px] text-white outline-none placeholder:text-white/60"
          />
        </div>

        <div className="max-h-64 overflow-y-auto">
          {loadingPublic && tab === "public" && publicRows.length === 0 ? (
            <div className="px-2 py-3 text-xs text-white/80">Loading skills…</div>
          ) : rows.length === 0 ? (
            <div className="px-2 py-3 text-xs text-white/80">
              {tab === "mine"
                ? "No skills added yet. Browse Public to add some."
                : "No skills found."}
            </div>
          ) : (
            <div className="flex flex-col">
              {rows.map((r) => {
                const RowIcon = iconForSkill(r.name);
                return (
                  <div
                    key={r.id}
                    className="group flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-white transition-colors hover:bg-white/[0.05]"
                  >
                    <RowIcon className="size-3.5 shrink-0 text-emerald-400" />
                    <button
                      type="button"
                      onClick={() => pick({ id: r.id, name: r.name })}
                      className="flex flex-1 items-center gap-1.5 truncate text-left"
                    >
                      <span className="truncate font-medium">{r.name}</span>
                      <Zap className="size-3 shrink-0 text-emerald-400 opacity-0 transition-opacity group-hover:opacity-100" />
                    </button>
                    <button
                      type="button"
                      aria-label={`What does ${r.name} do?`}
                      onClick={() => openDetail(r.id)}
                      className="shrink-0 rounded p-0.5 text-white/60 transition-colors hover:text-white"
                    >
                      <HelpCircle className="size-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="my-1.5 h-px bg-white/[0.06]" />

        {/* Pinned: arm the CURRENT chat with the Create a Skill builder (same
            path as picking any skill row). The arrow opens its full page. */}
        <div className="flex items-center gap-1.5 rounded-md border border-emerald-400/20 bg-emerald-500/[0.06] px-2 py-1.5">
          <Hammer className="size-3.5 shrink-0 text-emerald-300" />
          <button
            type="button"
            onClick={() => {
              // Arm the create-a-skill builder on THIS chat's composer instead
              // of spawning a fresh home chat. `pick` closes the menu and routes
              // through onPick (arm current composer); the user then describes
              // what to build and it runs in this conversation.
              pick({ id: CREATE_SKILL_ID, name: "Create a Skill" });
            }}
            className="flex flex-1 items-center truncate text-left"
          >
            <span className="truncate text-[13px] font-semibold text-white">
              Create a skill
            </span>
          </button>
          <button
            type="button"
            aria-label="What does Create a Skill do?"
            onClick={() => openDetail(CREATE_SKILL_ID)}
            className="shrink-0 rounded p-0.5 text-white/60 transition-colors hover:text-white"
          >
            <HelpCircle className="size-3.5" />
          </button>
        </div>

        {/* Browse the full library (all public skills, signals, automations). */}
        <button
          type="button"
          onClick={() => {
            setOpen(false);
            router.push("/library?tab=skill");
          }}
          className="mt-1.5 flex w-full items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-[11px] font-semibold text-white/70 transition-colors hover:bg-white/[0.05] hover:text-white"
        >
          <Library className="size-3.5" />
          Browse the full library
        </button>
    </>
  );

  // Mobile: the desktop popover is a fixed 320px anchored menu that gets
  // clipped / keyboard-covered on phones and pushes the "Create a skill" CTA
  // below the fold. On mobile we render the SAME content inside a responsive,
  // fully scrollable modal so everything (including Create a skill) is reachable.
  if (isMobile) {
    return (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>{trigger}</DialogTrigger>
        <DialogContent className="max-h-[80vh] w-full max-w-[calc(100vw-1.5rem)] gap-0 overflow-y-auto rounded-2xl border-white/10 bg-[#0c0c0e] p-2.5 text-white">
          <DialogTitle className="sr-only">Skills</DialogTitle>
          {body}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>{trigger}</PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="top">Browse skills</TooltipContent>
      </Tooltip>
      <PopoverContent
        align="start"
        side="top"
        className="w-80 rounded-lg border border-white/10 bg-[#0c0c0e] p-1.5 text-white shadow-2xl"
      >
        {body}
      </PopoverContent>
    </Popover>
  );
}
